import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface MarineSpecies {
  id: number;
  name: string;
  scientificName: string;
  image: string;
  alt: string;
  seasonTag: string;
  seasonTagType: 'year-round' | 'seasonal' | 'common';
  bestSites: {label: string;href: string;}[];
  description: string;
  speciesHref: string;
}

interface MarineLifeSpotlightProps {
  className?: string;
}

const SEASON_TAG_STYLES: Record<string, {bg: string;color: string;}> = {
  'year-round': { bg: 'rgba(111,211,224,0.15)', color: '#6FD3E0' },
  'seasonal': { bg: 'rgba(255,138,61,0.15)', color: '#FF8A3D' },
  'common': { bg: 'rgba(74,222,128,0.15)', color: '#4ade80' }
};

const MarineLifeSpotlight = ({ className = '' }: MarineLifeSpotlightProps) => {
  const species: MarineSpecies[] = [
  {
    id: 1,
    name: 'Green Sea Turtle',
    scientificName: 'Chelonia mydas',
    image: '/assets/images/cyprus-green-sea-turtle-underwater_scuba-1777384551987.jpg',
    alt: 'Green sea turtle (Chelonia mydas) swimming in clear Cyprus Mediterranean waters',
    seasonTag: 'Year-round',
    seasonTagType: 'year-round',
    bestSites: [
    { label: 'Akrotiri Peninsula', href: '/dive-sites/limassol' },
    { label: 'Cape Greco', href: '/dive-sites/ayia-napa-protaras' }],

    description: 'One of the most iconic marine encounters in Cyprus. Green sea turtles are regularly spotted gliding through clear coastal waters, particularly around protected bays and rocky reefs.',
    speciesHref: '/marine-life'
  },
  {
    id: 2,
    name: 'Dusky Grouper',
    scientificName: 'Epinephelus marginatus',
    image: '/assets/images/dusky-grouper-cyprus-epinephelus-marginatus-1777436562350.jpg',
    alt: 'Dusky Grouper (Epinephelus marginatus) close-up on a Cyprus reef',
    seasonTag: 'Year-round',
    seasonTagType: 'year-round',
    bestSites: [
    { label: 'Zenobia Wreck', href: '/dive-sites/zenobia' },
    { label: 'Jubilee Shoals', href: '/jubilee-shoals-cyprus' }],

    description: 'A large and iconic Mediterranean reef predator, often found resting near rocky reefs, caves, and wrecks. Surprisingly curious around divers and a highlight of many Cyprus dives.',
    speciesHref: '/marine-life'
  },
  {
    id: 3,
    name: 'Barracuda',
    scientificName: 'Sphyraena sphyraena',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_198908d2b-1777438020397.png",
    alt: 'School of barracuda in clear blue Mediterranean water Cyprus',
    seasonTag: 'Seasonal',
    seasonTagType: 'seasonal',
    bestSites: [
    { label: 'Akrotiri Peninsula', href: '/dive-sites/limassol' },
    { label: 'Cape Greco', href: '/dive-sites/ayia-napa-protaras' }],

    description: 'Sleek, fast-moving predators that often form impressive schools in open water. Most commonly encountered during summer months around deeper reef edges and open water sites.',
    speciesHref: '/marine-life'
  },
  {
    id: 4,
    name: 'Lionfish',
    scientificName: 'Pterois miles',
    image: '/assets/images/lionfish-cyprus-pterois-miles-invasive-1777436793885.jpg',
    alt: 'Lionfish (Pterois miles) invasive species in Cyprus waters with venomous spines',
    seasonTag: 'Year-round',
    seasonTagType: 'year-round',
    bestSites: [
    { label: 'Zenobia Wreck', href: '/dive-sites/zenobia' },
    { label: 'MUSAN', href: '/musan-underwater-museum-cyprus' }],

    description: 'An invasive species now established in Cyprus waters. Striking in appearance with bold red and white stripes and venomous spines. Do not touch — maintain a safe distance.',
    speciesHref: '/marine-life'
  },
  {
    id: 5,
    name: 'Common Octopus',
    scientificName: 'Octopus vulgaris',
    image: '/assets/images/octopus-cyprus-diving-mediterranean-1777437432495.jpg',
    alt: 'Common octopus (Octopus vulgaris) on sandy seabed in Cyprus Mediterranean waters',
    seasonTag: 'Year-round',
    seasonTagType: 'year-round',
    bestSites: [
    { label: 'Cape Greco', href: '/dive-sites/ayia-napa-protaras' },
    { label: 'Green Bay', href: '/green-bay-diving-cyprus' }],

    description: 'One of the most intelligent and fascinating species in Cyprus waters. Known for remarkable camouflage, octopuses are often spotted hiding in crevices or moving across the seabed.',
    speciesHref: '/marine-life'
  },
  {
    id: 6,
    name: 'Saddled Seabream',
    scientificName: 'Oblada melanura',
    image: '/assets/images/cyprus-saddled-seabream-underwater-1777361247864.jpg',
    alt: 'Saddled seabream (Oblada melanura) swimming over rocky reef in Cyprus',
    seasonTag: 'Common',
    seasonTagType: 'common',
    bestSites: [
    { label: 'Green Bay', href: '/green-bay-diving-cyprus' },
    { label: 'Larnaca Reefs', href: '/dive-sites/larnaca' }],

    description: 'One of the most commonly encountered fish on Cyprus reefs. Recognisable by the distinctive black spot at the base of its tail, often seen in large shoals around rocky and sandy areas.',
    speciesHref: '/marine-life'
  }];


  return (
    <section
      className={`py-12 sm:py-14 md:py-16 ${className}`}
      style={{
        background: '#0E1C2B',
        borderTop: '1px solid rgba(255,255,255,0.06)'
      }}>

      <div className="container mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="mb-8 sm:mb-10 md:mb-12">
          {/* Intro line */}
          <p
            className="text-sm sm:text-base font-medium mb-3"
            style={{ color: '#FF8A3D' }}>

            What you&apos;ll actually see underwater in Cyprus
          </p>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-headline font-bold mb-3 sm:mb-4"
            style={{ color: '#EAF4F8' }}>

            Cyprus Marine Life
          </h2>
          {/* SEO intro text with contextual links */}
          <p
            className="text-sm sm:text-base max-w-3xl leading-relaxed"
            style={{ color: '#C8DDE6' }}>

            <Link
              href="/marine-life"
              className="font-semibold underline underline-offset-2 transition-colors duration-150 hover:opacity-80"
              style={{ color: '#6FD3E0' }}>

              Marine life in Cyprus
            </Link>
            {' '}includes sea turtles, groupers, octopus, and invasive species like{' '}
            <Link
              href="/marine-life"
              className="font-semibold underline underline-offset-2 transition-colors duration-150 hover:opacity-80"
              style={{ color: '#6FD3E0' }}>

              lionfish
            </Link>
            , offering diverse underwater encounters year-round. The{' '}
            <Link
              href="/dive-sites/zenobia"
              className="font-semibold underline underline-offset-2 transition-colors duration-150 hover:opacity-80"
              style={{ color: '#6FD3E0' }}>

              Zenobia wreck
            </Link>
            {' '}and{' '}
            <Link
              href="/dive-sites/ayia-napa-protaras"
              className="font-semibold underline underline-offset-2 transition-colors duration-150 hover:opacity-80"
              style={{ color: '#6FD3E0' }}>

              Cape Greco
            </Link>
            {' '}are among the best sites for marine life encounters.
          </p>
        </div>

        {/* Species Grid — 1 col mobile, 2 col sm, 3 col lg */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 mb-6 sm:mb-8">
          {species.map((s) => {
            const tagStyle = SEASON_TAG_STYLES[s.seasonTagType];
            return (
              <div
                key={s.id}
                className="rounded-xl overflow-hidden transition-all duration-300 flex flex-col"
                style={{
                  background: '#0F2234',
                  border: '1px solid rgba(255,255,255,0.06)'
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.border = '1px solid rgba(111,211,224,0.25)';
                  el.style.transform = 'translateY(-3px)';
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.border = '1px solid rgba(255,255,255,0.06)';
                  el.style.transform = 'translateY(0)';
                }}>

                {/* Species Image */}
                <div className="relative h-44 sm:h-48 overflow-hidden flex-shrink-0">
                  <AppImage
                    src={s.image}
                    alt={s.alt}
                    className="w-full h-full object-cover" />

                  {/* Season tag */}
                  <div
                    className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold"
                    style={{ background: tagStyle.bg, color: tagStyle.color, backdropFilter: 'blur(4px)' }}>

                    {s.seasonTag}
                  </div>
                </div>

                {/* Species Info */}
                <div className="p-4 flex flex-col flex-1">
                  <div className="mb-2">
                    <h3
                      className="font-headline font-semibold text-sm sm:text-base mb-0.5"
                      style={{ color: '#EAF4F8' }}>

                      {s.name}
                    </h3>
                    <p
                      className="text-xs italic"
                      style={{ color: '#A9C3CF' }}>

                      {s.scientificName}
                    </p>
                  </div>

                  <p
                    className="text-xs sm:text-sm mb-3 leading-relaxed line-clamp-3 flex-1"
                    style={{ color: '#C8DDE6' }}>

                    {s.description}
                  </p>

                  {/* Best Viewing Sites */}
                  <div className="mb-3">
                    <p className="text-xs mb-1.5 font-medium" style={{ color: '#A9C3CF' }}>Best viewing sites:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {s.bestSites.map((site) =>
                      <Link
                        key={site.label}
                        href={site.href}
                        className="text-xs px-2 py-1 rounded-full transition-colors hover:opacity-80"
                        style={{
                          background: 'rgba(111,211,224,0.08)',
                          color: '#6FD3E0',
                          border: '1px solid rgba(111,211,224,0.2)'
                        }}>

                          {site.label}
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Footer link */}
                  <div className="pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <Link
                      href={s.speciesHref}
                      className="text-xs font-medium flex items-center gap-1 transition-opacity hover:opacity-80"
                      style={{ color: '#6FD3E0' }}>

                      <span>View species profile</span>
                      <Icon name="ArrowRightIcon" size={12} />
                    </Link>
                  </div>
                </div>
              </div>);

          })}
        </div>

        {/* Conservation Message */}
        <div
          className="rounded-xl p-4 sm:p-5 md:p-6 mb-6 sm:mb-8"
          style={{
            background: '#0F2234',
            border: '1px solid rgba(111,211,224,0.15)'
          }}>

          <div className="flex items-start space-x-3 sm:space-x-4">
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: '#0D2030', border: '1px solid rgba(111,211,224,0.25)' }}>

              <Icon name="HeartIcon" size={22} style={{ color: '#6FD3E0' }} />
            </div>
            <div className="flex-1">
              <h3
                className="font-headline font-semibold text-sm sm:text-base mb-2"
                style={{ color: '#EAF4F8' }}>

                Marine Conservation in Cyprus
              </h3>
              <p
                className="text-xs sm:text-sm mb-3 sm:mb-4"
                style={{ color: '#C8DDE6' }}>

                Cyprus waters are home to protected species and marine reserves. As responsible divers, we play a role in preserving these underwater ecosystems for future generations.
              </p>
              <Link
                href="/cyprus-diving-information#conservation"
                className="inline-flex items-center space-x-2 text-sm font-medium transition-colors"
                style={{ color: '#FF8A3D' }}
                onMouseEnter={(e) => {(e.currentTarget as HTMLElement).style.color = '#ffaa6e';}}
                onMouseLeave={(e) => {(e.currentTarget as HTMLElement).style.color = '#FF8A3D';}}>

                <span>Learn About Conservation →</span>
              </Link>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            href="/marine-life"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-base transition-all duration-200 hover:opacity-90"
            style={{ background: '#6FD3E0', color: '#0B1622' }}>

            Explore Cyprus Marine Life →
          </Link>
        </div>
      </div>
    </section>);

};

export default MarineLifeSpotlight;